import React, { PropsWithChildren, useCallback, useEffect } from "react";
import { AppState } from "react-native";
import { gql } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import { useAtomValue } from "jotai";
import { serverInMaintenanceAtom } from "../atoms/maintenance";
import { MaintenanceScreen } from "../screens/MaintenanceScreen";

export const MAINTENANCE_PROBE_INTERVAL_MS = 30_000;

// Smallest valid GraphQL query: it only tells whether the server answers. Its outcome reaches
// serverInMaintenanceAtom through the maintenance link, like any other operation.
const MAINTENANCE_PROBE = gql`
    query MaintenanceProbe {
        __typename
    }
`;

/**
 * Replaces the whole app with the maintenance screen while the server is in maintenance (it
 * answers HTTP 503, see configs/maintenance-link.ts).
 *
 * While the screen shows, the server is probed every MAINTENANCE_PROBE_INTERVAL_MS while the app
 * is active, when the app comes back to the foreground, and when the user taps "Retry". Once the server answers again the
 * app mounts afresh, exactly as on a cold start: no screen is left stuck on data that failed to
 * load during the maintenance.
 */
const MaintenanceManager: React.FC<PropsWithChildren> = ({ children }) => {
    const isServerInMaintenance = useAtomValue(serverInMaintenanceAtom);
    const client = useApolloClient();

    const probeServer = useCallback(async () => {
        try {
            await client.query({
                query: MAINTENANCE_PROBE,
                fetchPolicy: "no-cache",
                // One answer is enough to know the server state: the RetryLink would only delay
                // it and multiply the requests sent during the maintenance.
                context: { skipRetry: true },
            });
        } catch {
            // Expected while the maintenance lasts: the maintenance link has already recorded
            // the outcome.
        }
    }, [client]);

    useEffect(() => {
        if (!isServerInMaintenance) {
            return;
        }
        const interval = setInterval(() => {
            // In the background nobody sees the screen: the app probes again when it comes back.
            if (AppState.currentState === "active") {
                void probeServer();
            }
        }, MAINTENANCE_PROBE_INTERVAL_MS);
        const appStateSubscription = AppState.addEventListener("change", (nextAppState) => {
            if (nextAppState === "active") {
                void probeServer();
            }
        });
        return () => {
            clearInterval(interval);
            appStateSubscription.remove();
        };
    }, [isServerInMaintenance, probeServer]);

    if (isServerInMaintenance) {
        return <MaintenanceScreen onRetry={probeServer} />;
    }

    return children;
};

export default MaintenanceManager;
